import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from '../../components/Header'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'
import { coursesAPI, getApiBaseUrlWithoutApi } from '../../config/api'
import 'primeicons/primeicons.css'

export default function ChallengesPage() {
  const { theme } = useTheme()
  const { isAuthenticated, user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [challenges, setChallenges] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Chỉ redirect khi auth đã load xong và user chưa authenticated
    // Tránh redirect khi đang loading (có thể user đang được load từ localStorage)
    if (!authLoading && !isAuthenticated) {
      navigate('/login')
    }
  }, [isAuthenticated, authLoading, navigate])

  useEffect(() => {
    // Chỉ fetch data khi auth đã load xong và user đã authenticated
    if (authLoading) return // Đợi auth load xong
    
    if (!isAuthenticated) {
      setLoading(false)
      return
    }

    if (!user?.id) return // Chưa có user id

    let isMounted = true // Flag để tránh setState sau khi unmount

    const fetchChallenges = async () => {
      try {
        setLoading(true)
        // Lấy trực tiếp danh sách video thử thách (không cần curriculum)
        const lessonsData = await coursesAPI.getCurriculumLessons()
        const lessons = Array.isArray(lessonsData) ? lessonsData : (lessonsData?.results || lessonsData?.data || [])
        
        const API_BASE_URL = getApiBaseUrlWithoutApi()
        
        // Xử lý image URL cho mỗi challenge
        const processedLessons = lessons.map((lesson: any) => {
          let imageUrl = lesson.image_url || lesson.image
          if (imageUrl && imageUrl.startsWith('/media/')) {
            imageUrl = `${API_BASE_URL}${imageUrl}`
          } else if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('/images/')) {
            imageUrl = `${API_BASE_URL}${imageUrl}`
          }
          return {
            ...lesson,
            image_url: imageUrl
          }
        })
        
        // Sắp xếp theo week_number và chỉ lấy 4 tuần đầu (hiển thị tất cả, kể cả khi bị khóa)
        const weeklyChallenges = processedLessons
          .filter((lesson: any) => lesson.week_number >= 1 && lesson.week_number <= 4)
          .sort((a: any, b: any) => a.week_number - b.week_number)
          .slice(0, 4)
        
        if (isMounted) {
          setChallenges(weeklyChallenges)
        }
      } catch (error) {
        if (isMounted) {
          setChallenges([])
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchChallenges()

    return () => {
      isMounted = false // Cleanup
    }
  }, [isAuthenticated, authLoading, user?.id]) // Chỉ dùng user?.id thay vì user object

  const getWeekEmoji = (week: number) => {
    switch (week) {
      case 1: return '🥉'
      case 2: return '🥈'
      case 3: return '🥇'
      case 4: return '🏆'
      default: return '📅'
    }
  }

  const getWeekDescription = (week: number) => {
    switch (week) {
      case 1: return 'Hoàn thành thử thách tuần đầu tiên'
      case 2: return 'Duy trì tập luyện 2 tuần liên tiếp'
      case 3: return 'Giữ vững kỷ luật suốt 3 tuần'
      case 4: return 'Hoàn thành trọn vẹn chuỗi 4 tuần thử thách'
      default: return ''
    }
  }
  return (
    <main className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-200'}`}>
      <Header />

      {/* Header Section */}
      <section className={`py-16 md:py-20 px-4 ${theme === 'dark' ? 'bg-gradient-to-b from-black to-gold-900/10' : 'bg-gradient-to-b from-gray-200 to-gold-100/20'}`}>
        <div className="container mx-auto text-center max-w-4xl">
          <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-4 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
            Thử Thách Tháng
          </h1>
          <p className={`text-lg md:text-xl ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            4 video thử thách - Unlock mỗi tuần
          </p>
        </div>
      </section>

      {/* Challenges Section */}
      <section className={`py-12 md:py-16 px-4 ${theme === 'dark' ? 'bg-black' : 'bg-gray-200'}`}>
        <div className="container mx-auto max-w-6xl">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-400 mx-auto mb-4"></div>
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Đang tải thử thách...</p>
            </div>
          ) : challenges.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              {challenges.map((challenge: any) => {
                const badgeInfo = challenge.badge_info
                const hasBadge = badgeInfo?.has_badge || false
                const isActive = challenge.is_active !== false // Mặc định là true nếu không có
                
                return (
                  <div
                    key={challenge.id}
                    className={`flex flex-col h-full rounded-xl overflow-hidden border-2 ${
                      isActive
                        ? theme === 'dark'
                          ? 'border-gold-500/50 bg-black/60'
                          : 'border-gold-400 bg-white'
                        : theme === 'dark'
                        ? 'border-gray-700/50 bg-black/40 opacity-80'
                        : 'border-gray-300 bg-gray-100 opacity-80'
                    } shadow-lg transition-all duration-300 hover:shadow-xl`}
                  >
                    {/* Image Section */}
                    {challenge.image_url && (
                      <div className="relative w-full aspect-[16/9] overflow-hidden">
                        <img
                          src={challenge.image_url}
                          alt={challenge.title || `Thử thách tuần ${challenge.week_number}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                        
                        {/* Badge Icon Overlay */}
                        <div className="absolute top-3 left-3">
                          <span className="text-3xl md:text-4xl flex-shrink-0 drop-shadow-lg">{getWeekEmoji(challenge.week_number)}</span>
                        </div>
                        
                        {/* Badge Status Overlay */}
                        {hasBadge && (
                          <div className="absolute top-3 right-3">
                            <div className={`px-3 py-1 rounded-full flex-shrink-0 ${theme === 'dark' ? 'bg-gold-500/30 backdrop-blur-sm' : 'bg-gold-400/30 backdrop-blur-sm'}`}>
                              {badgeInfo?.is_emoji ? (
                                <span className="text-gold-400 text-xl block">{badgeInfo.icon}</span>
                              ) : (
                                <i className={`pi ${badgeInfo?.icon || 'pi-star'} text-gold-400 text-xl`}></i>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Header với emoji và badge */}
                    <div className={`flex-1 flex flex-col p-6 ${theme === 'dark' ? 'bg-gold-900/20' : 'bg-gold-100/50'}`}>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3 flex-1">
                          {!challenge.image_url && (
                            <span className="text-4xl flex-shrink-0">{getWeekEmoji(challenge.week_number)}</span>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className={`text-xl md:text-2xl font-bold mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {challenge.title || `TUẦN ${challenge.week_number}`}
                            </h3>
                            {(challenge.badge_name || badgeInfo?.name) && (
                              <p className={`text-sm font-semibold truncate ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}>
                                {challenge.badge_name || badgeInfo?.name}
                              </p>
                            )}
                          </div>
                        </div>
                        {hasBadge && !challenge.image_url && (
                          <div className={`px-3 py-1 rounded-full flex-shrink-0 ${theme === 'dark' ? 'bg-gold-500/30' : 'bg-gold-400/30'}`}>
                            {badgeInfo?.is_emoji ? (
                              <span className="text-gold-400 text-xl block">{badgeInfo.icon}</span>
                            ) : (
                              <i className={`pi ${badgeInfo?.icon || 'pi-star'} text-gold-400 text-xl`}></i>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Mô tả */}
                      {(challenge.challenge_description || badgeInfo?.description || getWeekDescription(challenge.week_number)) && (
                        <p className={`text-sm md:text-base mb-3 line-clamp-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          {challenge.challenge_description || badgeInfo?.description || getWeekDescription(challenge.week_number)}
                        </p>
                      )}
                      
                      {/* Điều kiện */}
                      {(challenge.conditions_text || badgeInfo?.requirement) && (
                        <div className={`p-3 rounded-lg mt-auto ${theme === 'dark' ? 'bg-black/50' : 'bg-white/80'}`}>
                          <p className={`text-xs font-semibold mb-2 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}>
                            Điều kiện nhận:
                          </p>
                          <ul className={`text-xs space-y-1.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            {(challenge.conditions_text || badgeInfo?.requirement || '').split('•').map((req: string, idx: number) => {
                              const trimmed = req.trim()
                              if (!trimmed) return null
                              return (
                                <li key={idx} className="flex items-start gap-2">
                                  <span className="text-gold-400 mt-0.5 flex-shrink-0">•</span>
                                  <span className="flex-1">{trimmed}</span>
                                </li>
                              )
                            })}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Video/Content */}
                    <div className="p-6 flex flex-col gap-3">
                      {!isActive && (
                        <div className="text-center py-2">
                          <i className="pi pi-lock text-3xl text-gold-400 mb-2 block"></i>
                          <p className={`text-sm font-semibold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            Lock chưa mở
                          </p>
                        </div>
                      )}
                      
                      {/* Primary Action Button */}
                      <button
                        onClick={() => navigate(`/challenges/${challenge.id}`)}
                        className={`w-full px-6 py-3 rounded-lg font-bold transition-all duration-300 flex items-center justify-center gap-2 ${
                          hasBadge
                            ? 'bg-green-500/20 text-green-400 border-2 border-green-500/50 hover:bg-green-500/30'
                            : !isActive
                            ? 'bg-gray-400/20 text-gray-500 border-2 border-gray-400/50 cursor-not-allowed'
                            : 'bg-gold-gradient text-white hover:shadow-lg hover:scale-105 active:scale-95'
                        }`}
                        disabled={!isActive}
                      >
                        {hasBadge ? (
                          <>
                            <i className="pi pi-check-circle text-lg"></i>
                            <span>Đã hoàn thành</span>
                          </>
                        ) : !isActive ? (
                          <>
                            <i className="pi pi-lock text-lg"></i>
                            <span>Lock chưa mở</span>
                          </>
                        ) : (
                          <>
                            <i className="pi pi-play text-lg"></i>
                            <span>Tham gia thử thách</span>
                          </>
                        )}
                      </button>

                      {/* Secondary CTA - Bảng Thành Tích */}
                      <button
                        onClick={() => navigate(`/challenges/${challenge.id}`)}
                        className={`w-full px-6 py-3 rounded-lg font-bold transition-all duration-300 flex items-center justify-center gap-2 border-2 hover:scale-105 active:scale-95 ${
                          theme === 'dark'
                            ? 'border-gold-500/50 text-gold-400 hover:bg-gold-500/10 hover:border-gold-400'
                            : 'border-gold-400 text-gold-600 hover:bg-gold-400/10 hover:border-gold-500'
                        }`}
                      >
                        <i className="pi pi-trophy text-lg"></i>
                        <span>Bảng Thành Tích</span>
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className={`text-center py-16 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              <i className="pi pi-trophy text-6xl mb-4 text-gold-400"></i>
              <p className="text-lg">Chưa có thử thách nào</p>
            </div>
          )}
        </div>
      </section>
    </main>
  )
}
